<template>
    <div class="w-full widget-time-absolute">
        <VcDatePicker
            :model-value="dateRange"
            type="daterange"
            :clearable="false"
            class="max-w-full"
            :disabled-date="disabledDate"
            :range-separator="defaultRangeSeparator"
            @update:model-value="onChange"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { WidgetTime } from '@/types/dashboard'
import { VcDatePicker } from '@voicenter-team/voicenter-ui-plus'
import type { TRangeDateType, TSingleDateType } from '@voicenter-team/voicenter-ui-plus'
import { useVModel } from '@vueuse/core'
import { dayjs } from '@/composables/useDayjs'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = withDefaults(
    defineProps<{
        modelValue: WidgetTime
        widgetTimeOptions?: Array<Record<string, string>>
        rangeSeparator?: string
    }>(),
    {
        widgetTimeOptions: () => [],
        rangeSeparator: undefined
    }
)

const emit = defineEmits<{
    (e: 'update:modelValue', v: WidgetTime): void
}>()

const timeModel = useVModel(props, 'modelValue', emit)

const defaultRangeSeparator = computed(() => {
    return props.rangeSeparator ?? String(t('range.filter.to'))
})

function disabledDate (time: Date) {
    const date = new Date()
    const dateAfterDays = date.setDate(date.getDate() + 90)
    return time.getTime() > dateAfterDays
}

function computeDateRange (): TRangeDateType {
    const widgetTime = timeModel.value

    if (widgetTime.Date_start && widgetTime.Date_end) {
        const storedStartDate = dayjs(widgetTime.Date_start)
        const storedEndDate = dayjs(widgetTime.Date_end)

        if (storedStartDate.isValid() && storedEndDate.isValid()) {
            return [ storedStartDate.startOf('day').toDate(), storedEndDate.startOf('day').toDate() ]
        }
    }

    const dateInterval = Math.floor(Number(widgetTime.Date_interval || '0'))
    const dateDifference = Number(widgetTime.datedeff || '0')
    const startDate = dayjs().startOf('day').subtract(dateInterval, 'day')
    const endDate = startDate.add(dateDifference, 'day')

    return [ startDate.toDate(), endDate.toDate() ]
}

const dateRange = computed<TRangeDateType>(() => {
    return computeDateRange()
})

function onChange (dates: TRangeDateType | TSingleDateType) {
    if (!dates || !Array.isArray(dates) || dates.length < 2 || !dates[0] || !dates[1]) return

    const dateStart = dayjs(dates[0]).startOf('day')
    const dateEnd = dayjs(dates[1]).startOf('day')

    timeModel.value.datedeff = dateEnd.diff(dateStart, 'day').toString()
    timeModel.value.Date_interval = dayjs().startOf('day').diff(dateStart, 'day').toString()

    timeModel.value.Date_start = dateStart.format('YYYY-MM-DD')
    timeModel.value.Date_end = dateEnd.format('YYYY-MM-DD')
}

onMounted(() => {
    // Initialize Date_start and Date_end on mount (matching original DateTimePicker behavior)
    const [ start, end ] = computeDateRange()
    onChange([ start, end ])
})
</script>
